/**
 * Doctor Command
 * System diagnostics and health check
 */

import kleur from 'kleur';
import { execSync } from 'child_process';
import fs from 'fs';
import path from 'path';
import os from 'os';
import { getHookManager } from '../../hooks';
import { getCheckpointManager } from '../../checkpoints';
import { getSandboxManager } from '../../sandbox';
import { getRulesLoader } from '../../runtime/rulesLoader';

interface DiagnosticResult {
  name: string;
  status: 'ok' | 'warning' | 'error' | 'info';
  message: string;
  details?: string;
}

export async function runDoctor(args: string[]): Promise<void> {
  console.log(kleur.cyan('\n🩺 Metis Code Doctor'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.gray('Running system diagnostics...'));
  console.log();

  const results: DiagnosticResult[] = [];

  // Check Node.js version
  results.push(checkNodeVersion());

  // Check npm
  results.push(checkNpm());

  // Check Git
  results.push(checkGit());

  // Check ripgrep
  results.push(checkRipgrep());

  // Check API keys
  results.push(...checkApiKeys());

  // Check configuration
  results.push(...checkConfiguration());

  // Check features
  results.push(...checkFeatures());

  // Check disk space
  results.push(checkDiskSpace());

  // Print results
  printResults(results);

  // Summary
  const errors = results.filter(r => r.status === 'error').length;
  const warnings = results.filter(r => r.status === 'warning').length;

  console.log();
  console.log(kleur.gray('─'.repeat(60)));

  if (errors > 0) {
    console.log(kleur.red(`❌ ${errors} error(s) found`));
  }
  if (warnings > 0) {
    console.log(kleur.yellow(`⚠️  ${warnings} warning(s) found`));
  }
  if (errors === 0 && warnings === 0) {
    console.log(kleur.green('✅ All checks passed'));
  }
}

function checkNodeVersion(): DiagnosticResult {
  const version = process.version;
  const major = parseInt(version.slice(1).split('.')[0], 10);

  if (major >= 18) {
    return {
      name: 'Node.js',
      status: 'ok',
      message: `Version ${version}`,
    };
  } else if (major >= 16) {
    return {
      name: 'Node.js',
      status: 'warning',
      message: `Version ${version} (18+ recommended)`,
    };
  } else {
    return {
      name: 'Node.js',
      status: 'error',
      message: `Version ${version} (18+ required)`,
    };
  }
}

function checkNpm(): DiagnosticResult {
  try {
    const version = execSync('npm --version', { encoding: 'utf8' }).trim();
    return {
      name: 'npm',
      status: 'ok',
      message: `Version ${version}`,
    };
  } catch {
    return {
      name: 'npm',
      status: 'error',
      message: 'Not found',
      details: 'npm is required for package management',
    };
  }
}

function checkGit(): DiagnosticResult {
  try {
    const version = execSync('git --version', { encoding: 'utf8' }).trim();
    const versionMatch = version.match(/\d+\.\d+\.\d+/);

    // Check if in a git repo
    let isRepo = false;
    try {
      execSync('git rev-parse --git-dir', { stdio: 'pipe' });
      isRepo = true;
    } catch {
      // Not in a repo
    }

    return {
      name: 'Git',
      status: 'ok',
      message: versionMatch ? `Version ${versionMatch[0]}` : version,
      details: isRepo ? 'In git repository' : 'Not in git repository',
    };
  } catch {
    return {
      name: 'Git',
      status: 'warning',
      message: 'Not found',
      details: 'Git is recommended for version control',
    };
  }
}

function checkRipgrep(): DiagnosticResult {
  try {
    const version = execSync('rg --version', { encoding: 'utf8' })
      .split('\n')[0]
      .trim();

    return {
      name: 'ripgrep',
      status: 'ok',
      message: version,
    };
  } catch {
    return {
      name: 'ripgrep',
      status: 'warning',
      message: 'Not found',
      details: 'Install ripgrep for faster search (falls back to grep)',
    };
  }
}

function checkApiKeys(): DiagnosticResult[] {
  const results: DiagnosticResult[] = [];

  // Check environment variables
  const envKeys = {
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
  };

  let hasAnyKey = false;
  const keyStatus: string[] = [];

  for (const [name, value] of Object.entries(envKeys)) {
    if (value) {
      hasAnyKey = true;
      const masked = `${value.substring(0, 8)}...`;
      keyStatus.push(`${name}: ${masked}`);
    }
  }

  // Check global config
  const globalSecretsPath = path.join(os.homedir(), '.metis', 'secrets.json');
  if (fs.existsSync(globalSecretsPath)) {
    try {
      const secrets = JSON.parse(fs.readFileSync(globalSecretsPath, 'utf8'));
      if (Object.keys(secrets).length > 0) {
        hasAnyKey = true;
        keyStatus.push('Global config: configured');
      }
    } catch {
      // Ignore
    }
  }

  // Check local config
  const localSecretsPath = path.join(process.cwd(), '.metis', 'secrets.json');
  if (fs.existsSync(localSecretsPath)) {
    try {
      const secrets = JSON.parse(fs.readFileSync(localSecretsPath, 'utf8'));
      if (Object.keys(secrets).length > 0) {
        hasAnyKey = true;
        keyStatus.push('Local config: configured');
      }
    } catch {
      // Ignore
    }
  }

  results.push({
    name: 'API Keys',
    status: hasAnyKey ? 'ok' : 'error',
    message: hasAnyKey ? 'Configured' : 'No API keys found',
    details: keyStatus.length > 0 ? keyStatus.join(', ') : 'Run /auth to configure',
  });

  return results;
}

function checkConfiguration(): DiagnosticResult[] {
  const results: DiagnosticResult[] = [];

  // Check global config
  const globalConfigPath = path.join(os.homedir(), '.metis', 'config.json');
  if (fs.existsSync(globalConfigPath)) {
    results.push({
      name: 'Global Config',
      status: 'ok',
      message: 'Found',
      details: globalConfigPath,
    });
  } else {
    results.push({
      name: 'Global Config',
      status: 'info',
      message: 'Not found (using defaults)',
    });
  }

  // Check local config
  const localConfigPath = path.join(process.cwd(), 'metis.config.json');
  if (fs.existsSync(localConfigPath)) {
    results.push({
      name: 'Project Config',
      status: 'ok',
      message: 'Found',
      details: localConfigPath,
    });
  }

  // Check Agent.md
  const agentMdPath = path.join(process.cwd(), 'Agent.md');
  if (fs.existsSync(agentMdPath)) {
    const stats = fs.statSync(agentMdPath);
    const lines = fs.readFileSync(agentMdPath, 'utf8').split('\n').length;
    results.push({
      name: 'Agent.md',
      status: 'ok',
      message: `Found (${lines} lines)`,
    });
  } else {
    results.push({
      name: 'Agent.md',
      status: 'info',
      message: 'Not found',
      details: 'Run /init to create',
    });
  }

  return results;
}

function checkFeatures(): DiagnosticResult[] {
  const results: DiagnosticResult[] = [];

  // Check hooks
  try {
    const hookManager = getHookManager();
    const hooks = hookManager.getHooks();
    results.push({
      name: 'Hooks',
      status: hookManager.isEnabled() ? 'ok' : 'info',
      message: hookManager.isEnabled() ? `Enabled (${hooks.length} hooks)` : 'Disabled',
    });
  } catch {
    results.push({
      name: 'Hooks',
      status: 'info',
      message: 'Not initialized',
    });
  }

  // Check checkpoints
  try {
    const checkpointManager = getCheckpointManager();
    const summary = checkpointManager.getCheckpointsSummary();
    results.push({
      name: 'Checkpoints',
      status: checkpointManager.isEnabled() ? 'ok' : 'info',
      message: checkpointManager.isEnabled()
        ? `Enabled (${summary.total} checkpoints)`
        : 'Disabled',
    });
  } catch {
    results.push({
      name: 'Checkpoints',
      status: 'info',
      message: 'Not initialized',
    });
  }

  // Check sandbox
  try {
    const sandboxManager = getSandboxManager();
    const caps = sandboxManager.getCapabilities();
    results.push({
      name: 'Sandbox',
      status: caps.available ? 'ok' : 'warning',
      message: caps.available
        ? `Available (${caps.recommended})`
        : 'Not available',
      details: caps.reason,
    });
  } catch {
    results.push({
      name: 'Sandbox',
      status: 'info',
      message: 'Not initialized',
    });
  }

  // Check rules
  try {
    const rulesLoader = getRulesLoader();
    const rules = rulesLoader.getRulesList();
    results.push({
      name: 'Rules',
      status: rules.length > 0 ? 'ok' : 'info',
      message: rules.length > 0 ? `${rules.length} rule file(s)` : 'No rules',
    });
  } catch {
    results.push({
      name: 'Rules',
      status: 'info',
      message: 'Not initialized',
    });
  }

  return results;
}

function checkDiskSpace(): DiagnosticResult {
  try {
    const cwd = process.cwd();

    // Get disk usage (platform-specific)
    if (os.platform() === 'win32') {
      return {
        name: 'Disk Space',
        status: 'info',
        message: 'Check skipped (Windows)',
      };
    }

    const output = execSync(`df -h "${cwd}"`, { encoding: 'utf8' });
    const lines = output.trim().split('\n');
    if (lines.length >= 2) {
      const parts = lines[1].split(/\s+/);
      const available = parts[3];
      const usePercent = parts[4];

      const useNum = parseInt(usePercent, 10);
      const status = useNum > 90 ? 'warning' : 'ok';

      return {
        name: 'Disk Space',
        status,
        message: `${available} available (${usePercent} used)`,
      };
    }

    return {
      name: 'Disk Space',
      status: 'info',
      message: 'Could not determine',
    };
  } catch {
    return {
      name: 'Disk Space',
      status: 'info',
      message: 'Check failed',
    };
  }
}

function printResults(results: DiagnosticResult[]): void {
  for (const result of results) {
    const icon = getStatusIcon(result.status);
    const statusColor = getStatusColor(result.status);

    console.log(
      icon + ' ' +
      kleur.white(result.name.padEnd(15)) +
      statusColor(result.message)
    );

    if (result.details) {
      console.log(kleur.gray(`   ${result.details}`));
    }
  }
}

function getStatusIcon(status: DiagnosticResult['status']): string {
  switch (status) {
    case 'ok': return kleur.green('✓');
    case 'warning': return kleur.yellow('⚠');
    case 'error': return kleur.red('✗');
    case 'info': return kleur.blue('ℹ');
  }
}

function getStatusColor(status: DiagnosticResult['status']): (text: string) => string {
  switch (status) {
    case 'ok': return kleur.green;
    case 'warning': return kleur.yellow;
    case 'error': return kleur.red;
    case 'info': return kleur.gray;
  }
}
